import React, { useState, useEffect } from 'react';
import { getBlogPosts, saveBlogPost, deleteBlogPost } from '../../services/blogService';
import type { BlogPost } from '../../types';
import { useToast } from '../../hooks/useToast';
import { TrashIcon, EditIcon, PlusIcon } from 'lucide-react';

export const BlogPanel: React.FC = () => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingPost, setEditingPost] = useState<BlogPost | null>(null);
  const { showToast } = useToast();

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    setLoading(true);
    const fetchedPosts = await getBlogPosts(false);
    setPosts(fetchedPosts);
    setLoading(false);
  };

  const [categoriesStr, setCategoriesStr] = useState('');
  const [tagsStr, setTagsStr] = useState('');

  const handleEdit = (post: BlogPost) => {
    setEditingPost(post);
    setCategoriesStr(post.categories.join(', '));
    setTagsStr(post.tags.join(', '));
  };

  const handleCreate = () => {
    const newPost: BlogPost = {
      id: Date.now().toString(),
      slug: '',
      title: '',
      excerpt: '',
      content: '',
      author: 'Admin',
      categories: [],
      tags: [],
      status: 'draft',
      publishedAt: new Date().toISOString(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      seoTitle: '',
      seoDescription: '',
    };
    setEditingPost(newPost);
    setCategoriesStr('');
    setTagsStr('');
  };

  const handleDelete = async (id: string) => {
    if (window.confirm("Are you sure you want to delete this post?")) {
      await deleteBlogPost(id);
      showToast('Post deleted!');
      fetchPosts();
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editingPost) {
      if (!editingPost.slug) {
        showToast('Slug is required', 'error');
        return;
      }
      
      const finalCategories = categoriesStr.split(',').map(s => s.trim()).filter(Boolean);
      const finalTags = tagsStr.split(',').map(s => s.trim()).filter(Boolean);

      const postToSave = {
        ...editingPost,
        categories: finalCategories,
        tags: finalTags,
        updatedAt: new Date().toISOString(),
        id: editingPost.id || editingPost.slug
      };
      
      await saveBlogPost(postToSave);
      showToast('Post saved successfully!');
      setEditingPost(null);
      fetchPosts();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    if (editingPost) {
      const { name, value } = e.target;
      if (name === 'categoriesStr') {
        setCategoriesStr(value);
      } else if (name === 'tagsStr') {
        setTagsStr(value);
      } else {
        setEditingPost({ ...editingPost, [name]: value });
      }
    }
  };

  if (loading) return <div>Loading...</div>;

  if (editingPost) {
    return (
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-indigo-700">
            {editingPost.id === editingPost.slug && editingPost.slug !== '' ? 'Edit Post' : 'Create Post'}
          </h2>
          <button onClick={() => setEditingPost(null)} className="text-gray-500 hover:text-gray-700">Cancel</button>
        </div>
        <form onSubmit={handleSave} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
              <input type="text" name="title" value={editingPost.title} onChange={handleChange} required className="w-full px-3 py-2 border border-gray-300 rounded-md" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Slug</label>
              <input type="text" name="slug" value={editingPost.slug} onChange={handleChange} required className="w-full px-3 py-2 border border-gray-300 rounded-md" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Excerpt</label>
              <textarea name="excerpt" value={editingPost.excerpt} onChange={handleChange} rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-md"></textarea>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Content (Markdown / HTML)</label>
              <textarea name="content" value={editingPost.content} onChange={handleChange} rows={10} className="w-full px-3 py-2 border border-gray-300 rounded-md"></textarea>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Categories (comma separated)</label>
              <input type="text" name="categoriesStr" value={categoriesStr} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tags (comma separated)</label>
              <input type="text" name="tagsStr" value={tagsStr} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Author</label>
              <input type="text" name="author" value={editingPost.author} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select name="status" value={editingPost.status} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md">
                <option value="draft">Draft</option>
                <option value="published">Published</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Featured Image URL</label>
              <input type="text" name="featuredImage" value={editingPost.featuredImage || ''} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">SEO Title</label>
              <input type="text" name="seoTitle" value={editingPost.seoTitle} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">SEO Description</label>
              <textarea name="seoDescription" value={editingPost.seoDescription} onChange={handleChange} rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-md"></textarea>
            </div>
          </div>
          <div className="pt-4 flex justify-end gap-2">
            <button type="button" onClick={() => setEditingPost(null)} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50">Cancel</button>
            <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700">Save Post</button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-indigo-700">Blog Management</h2>
        <button onClick={handleCreate} className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700">
          <PlusIcon className="w-4 h-4" /> New Post
        </button>
      </div>

      {posts.length === 0 ? (
        <p className="text-gray-500">No blog posts found.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {posts.map(post => (
                <tr key={post.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="font-medium text-gray-900">{post.title}</div>
                    <div className="text-sm text-gray-500">{post.slug}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${post.status === 'published' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                      {post.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(post.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button onClick={() => handleEdit(post)} className="text-indigo-600 hover:text-indigo-900 mr-4">Edit</button>
                    <button onClick={() => handleDelete(post.id)} className="text-red-600 hover:text-red-900">Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
